import React, { useEffect } from 'react';
import { Card, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Box, CircularProgress, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAdminData } from '../../../../contexts/AdminDataContext';
// Safely render nested or non-primitive values from API
const displayValue = (val) => {
    if (val === null || val === undefined || val === '') return 'N/A';
    if (Array.isArray(val)) {
        // If array of primitives
        const allPrimitive = val.every(v => v === null || ['string', 'number', 'boolean'].includes(typeof v));
        if (allPrimitive) return val.join(', ');
        // If array of objects, try to show their name fields
        const names = val.map(v => (v && typeof v === 'object' && 'name' in v) ? v.name : '').filter(Boolean);
        if (names.length) return names.join(', ');
        return 'N/A';
    }
    if (typeof val === 'object') {
        if ('name' in val && (typeof val.name === 'string' || typeof val.name === 'number')) return String(val.name);
        if ('title' in val && (typeof val.title === 'string' || typeof val.title === 'number')) return String(val.title);
        // Avoid rendering [object Object]
        return 'N/A';
    }
    return String(val);
};

// Specific formatting for status values
const formatStatus = (val) => {
    if (typeof val === 'boolean') return val ? 'ACTIVE' : 'INACTIVE';
    if (typeof val === 'number') return val === 1 ? 'ACTIVE' : val === 0 ? 'INACTIVE' : 'N/A';
    if (typeof val === 'string') {
        const v = val.trim().toLowerCase();
        if (['true', 'active', 'enabled', '1', 'yes'].includes(v)) return 'ACTIVE';
        if (['false', 'inactive', 'disabled', '0', 'no'].includes(v)) return 'INACTIVE';
        return val; // leave other strings as-is
    }
    if (val && typeof val === 'object') {
        if ('name' in val) return formatStatus(String(val.name));
    }
    return displayValue(val);
};

// Render colored status chip
const renderStatusChip = (val) => {
    const label = formatStatus(val);
    const isActive = label === 'ACTIVE';
    const isInactive = label === 'INACTIVE';
    const commonSx = { fontWeight: 600, borderWidth: 1 };
    if (isActive) {
        return (
            <Chip
                label={label}
                size="small"
                variant="outlined"
                sx={{
                    ...commonSx,
                    color: '#2ecc71',
                    borderColor: '#2ecc71',
                    bgcolor: 'rgba(46, 204, 113, 0.15)'
                }}
            />
        );
    }
    if (isInactive) {
        return (
            <Chip
                label={label}
                size="small"
                variant="outlined"
                sx={{
                    ...commonSx,
                    color: '#e74c3c',
                    borderColor: '#e74c3c',
                    bgcolor: 'rgba(231, 76, 60, 0.06)'
                }}
            />
        );
    }
    // Fallback for other labels
    return <Chip label={label} size="small" variant="outlined" sx={{ ...commonSx }} />;
};

const TableCard = styled(Card)(({ theme }) => ({
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    padding: '16px',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    border: '1px solid #f0f0f0',
    flex: 1,
    minHeight: '400px'
}));

const TableHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px'
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    borderRadius: '4px',
    border: '1px solid #f0f0f0'
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
    backgroundColor: '#0b91d6'
}));

const HeaderCell = styled(TableCell)(({ theme }) => ({
    fontFamily: 'Inter, sans-serif',
    fontSize: '16px',
    fontWeight: 500,
    color: '#ffffff',
    padding: '12px 16px',
    borderRight: '1px solid rgba(255, 255, 255, 0.2)',
    '&:last-child': {
        borderRight: 'none'
    }
}));

const DataCell = styled(TableCell)(({ theme }) => ({
    fontFamily: 'Inter, sans-serif',
    fontSize: '12px',
    fontWeight: 400,
    color: '#363838ff',
    padding: '12px 16px',
    borderBottom: '1px solid #f0f0f0',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
}));


const UserListing = () => {
    const { employees: cachedEmployees, loading: contextLoading, error: contextError, actions } = useAdminData();

    // Use cached data or fallback to empty/loading state
    const users = cachedEmployees || [];
    const loading = contextLoading.employees && users.length === 0;
    const error = contextError.employees || null;

    useEffect(() => {
        actions.fetchEmployees();
    }, [actions]);

    return (
        <TableCard>
            <TableHeader>
            </TableHeader>
            <StyledTableContainer>
                <Table>
                    <StyledTableHead>
                        <TableRow>
                            <HeaderCell>S.No</HeaderCell>
                            <HeaderCell>Name</HeaderCell>
                            <HeaderCell>Email</HeaderCell>
                            <HeaderCell>Type</HeaderCell>
                            <HeaderCell>Role</HeaderCell>
                            <HeaderCell>Status</HeaderCell>
                        </TableRow>
                    </StyledTableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <DataCell colSpan={8} style={{ textAlign: 'center' }}>
                                    <CircularProgress size={24} />
                                    <Typography variant="body2" style={{ marginTop: '8px' }}>
                                        Loading user data...
                                    </Typography>
                                </DataCell>
                            </TableRow>
                        ) : error ? (
                            <TableRow>
                                <DataCell colSpan={8} style={{ textAlign: 'center', color: 'red' }}>
                                    {error}
                                </DataCell>
                            </TableRow>
                        ) : users && users.length > 0 ? users.map((user, index) => (
                            <TableRow key={user.id || index}>
                                <DataCell>{index + 1}</DataCell>
                                <DataCell>{user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'N/A'}</DataCell>
                                <DataCell>{displayValue(user.email)}</DataCell>
                                <DataCell>{displayValue(user.type)}</DataCell>
                                <DataCell>{displayValue(user.role)}</DataCell>
                                <DataCell>{renderStatusChip(user.status)}</DataCell>
                            </TableRow>
                        )) : (
                            <TableRow>
                                <DataCell colSpan={8} style={{ textAlign: 'center' }}>
                                    No user data available
                                </DataCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </StyledTableContainer>
        </TableCard>
    );
};

export default UserListing;